import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Indicator } from '../models/indicator';
import { Category } from '../models/category';
import { computed } from '@angular/core';
import { Tag } from '../models/tag';
import { DataLayer } from '../models/data-layer';
import {
  SetSelectionRule,
  WHOLE_SETS_RULE,
} from './indicator/indicator-set-selection';

export const routeEntityTypeMapping: { [key: string]: BulkEntityTypes } = {
  indikatoren: 'Indicator',
  daten: 'IndicatorData',
  bereiche: 'Category',
};

export const routeActionMapping: { [key: string]: string } = {
  löschen: 'remove',
  sperren: 'lock',
  'intern schalten': 'unpublish',
  freigeben: 'unlock',
  veröffentlichen: 'publish',
  verschieben: 'move',
  versetten: 'addToSet',
  verschlagworten: 'addTags',
};

export const bulkSteps: any = {
  Indicator: {
    remove: ['pickIndicators', 'confirm'],
    lock: ['pickIndicators', 'confirm'],
    unlock: ['pickIndicators', 'confirm'],
    addTags: ['pickIndicators', 'pickTags', 'confirm'],
    addToSet: ['pickIndicators', 'pickSet', 'confirm'],
    move: ['pickIndicators', 'pickCategory', 'confirm'],
  },
  Category: {
    remove: ['pickCategories', 'confirm'],
    lock: ['pickCategories', 'confirm'],
    unlock: ['pickCategories', 'confirm'],
    move: ['pickCategories', 'pickCategory', 'confirm'],
  },
  IndicatorData: {
    remove: ['pickIndicators', 'pickDataLayers', 'confirm'],
    publish: ['pickIndicators', 'pickDataLayers', 'confirm'],
    unpublish: ['pickIndicators', 'pickDataLayers', 'confirm'],
  },
};

/**
 * What can be picked in the indicator pick list per bulk action.
 * A picked set is always the set with all of its children.
 */
const indicatorSetRules: Record<string, SetSelectionRule> = {
  lock: { sets: true, children: true },
  unlock: { sets: true, children: true },
  addTags: { sets: true, children: true },
  remove: { sets: true, children: true },
  move: { sets: true, children: false },
  addToSet: { sets: false, children: true },
};

/**
 * Returns the set rule for the indicator pick list of a bulk action.
 * Indicator data actions work on whole sets (sets have no data of their own).
 *
 * @param {string | undefined} entityType - The selected bulk entity type.
 * @param {string} action - The selected bulk action.
 * @return {SetSelectionRule} The rule for the pick list.
 */
export function getIndicatorSetRule(
  entityType: string | undefined,
  action: string
): SetSelectionRule {
  if (entityType !== 'Indicator') return WHOLE_SETS_RULE;
  return indicatorSetRules[action] ?? WHOLE_SETS_RULE;
}

export type BulkEntityTypes = 'Indicator' | 'Category' | 'IndicatorData';
export type BulkListTypes =
  | 'indicators'
  | 'categories'
  | 'dataLayers'
  | 'tags'
  | 'indicatorData';

export type BulkState = {
  currentStep: string;
  stepsToGo: string[];
  selectedEntityType: undefined | 'Indicator' | 'Category' | 'IndicatorData';
  selectedAction: string;
  indicators: Indicator[];
  categories: Category[];
  dataLayers: DataLayer[];
  tags: Tag[];
  set: null | Indicator;
  category: null | Category;
};

const initialState: BulkState = {
  currentStep: '',
  stepsToGo: [],
  selectedEntityType: undefined,
  selectedAction: '',
  indicators: [],
  categories: [],
  dataLayers: [],
  tags: [],
  set: null,
  category: null,
};

export const BulkActionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    indicatorIds: computed((): string[] => {
      return store.indicators().map(indicator => indicator._id);
    }),
    categoryIds: computed((): string[] => {
      return store.categories().map(category => category._id);
    }),
    dataLayerIds: computed((): string[] => {
      return store.dataLayers().map(dataLayer => dataLayer._id);
    }),
    getSteps: computed(() => {
      const entityType = store.selectedEntityType();
      const action = store.selectedAction();
      if (entityType) {
        return bulkSteps[entityType]?.[action] ?? [];
      }
    }),
    getNumberOfCurrentStep: computed(() => {
      let steps: string[] = [];
      const entityType = store.selectedEntityType();
      const action = store.selectedAction();
      if (entityType) {
        steps = bulkSteps[entityType]?.[action] ?? [];
        return steps.indexOf(store.currentStep());
      }
      return -1;
    }),
  })),
  withMethods(store => ({
    // Method to reset the state to initial values
    getStepLabel(step: string) {
      const stepLabels: { [key: string]: string } = {
        pickIndicators: 'Indikatoren auswählen',
        confirm: 'bestätigen',
        pickTags: 'Schlagworte auswählen',
        pickSet: 'Indikatorenset auswählen',
        pickCategories: 'Ordner auswählen',
        pickDataLayers: 'Jahre auswählen',
        pickCategory: 'verschieben in',
      };
      return step ? stepLabels[step] : '';
    },
    resetState() {
      patchState(store, initialState);
    },
  }))
);
